/**
 * Parallax polaroid-style floating image.
 * Uses scroll-linked transforms (y, x, rotate, scale, opacity) for a
 * depth-of-field effect. Wrapped in a cream-coloured "polaroid" frame.
 */
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import BlurImage from "./BlurImage";

interface FloatingImageProps {
  src: string;
  alt: string;
  className?: string;
  parallaxSpeed?: number;
  direction?: "left" | "right";
  rotation?: number;
}

const FloatingImage = ({
  src,
  alt,
  className = "",
  parallaxSpeed = 50,
  direction = "right",
  rotation = 3,
}: FloatingImageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Use transform instead of top/left for better performance (GPU accelerated)
  const y = useTransform(scrollYProgress, [0, 1], [parallaxSpeed, -parallaxSpeed]);
  const x = useTransform(
    scrollYProgress, 
    [0, 1], 
    direction === "right" ? [-20, 20] : [20, -20]
  );
  const rotate = useTransform(
    scrollYProgress, 
    [0, 1], 
    [-rotation, rotation]
  );
  
  // Fade in when entering viewport, fade out when leaving
  const opacity = useTransform(
    scrollYProgress, 
    [0, 0.15, 0.85, 1], 
    [0, 1, 1, 0]
  );
  
  // Scale for added depth
  const scale = useTransform(
    scrollYProgress,
    [0, 0.15, 0.85, 1],
    [0.9, 1, 1, 0.9]
  );

  return (
    <motion.div
      ref={ref}
      style={{ 
        y, 
        x, 
        rotate, 
        opacity, 
        scale,
        // Force GPU acceleration for smoother animation
        willChange: "transform, opacity",
      }}
      className={`absolute pointer-events-none z-0 ${className}`}
    >
      {/* Polaroid frame */}
      <div className="bg-[hsl(var(--garage-cream))] p-2 pb-8 shadow-[0_10px_40px_-10px_hsl(var(--background)/0.8),0_0_0_1px_hsl(var(--foreground)/0.06)]">
        <div className="relative overflow-hidden">
          <BlurImage
            src={src}
            alt={alt}
            className="w-full h-auto object-cover grayscale"
            loading="lazy"
          />
          {/* Vintage overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-background/30" />
        </div>
      </div>
    </motion.div>
  );
};

export default FloatingImage;
